// In-app vault file preview — works in Electron where window.open(blob:) is blocked.
import { useEffect, useRef, useState } from "react";

import { useI18n } from "~/i18n";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Spinner } from "../ui/spinner";
import {
  downloadVaultItem,
  fetchVaultItemAsFile,
  fetchVaultItemPreviewUrl,
  fetchVaultItemText,
  type VaultItem,
} from "../../morrowVault";
import {
  convertHeicPreviewUrl,
  extractPdfTextSnippet,
  fetchVaultOfficeSnippet,
  fetchVaultPdfEmbedUrl,
  fetchVaultSpreadsheetSnippet,
  fetchVaultTextSnippet,
  renderVaultPdfThumbnail,
} from "./vaultDocumentPreview";
import {
  isArchiveFile,
  isAudioMime,
  isHeicMime,
  isImageMime,
  isOfficeFile,
  isPdfMime,
  isSpreadsheetFile,
  isStructuredTextMime,
  isTextMime,
  isVideoMime,
  resolveVaultMimeType,
  typeLabel,
  usesVaultTextSnippetPreview,
} from "./vaultShared";

type PreviewState =
  | { readonly kind: "loading" }
  | { readonly kind: "image"; readonly url: string }
  | { readonly kind: "video"; readonly url: string }
  | { readonly kind: "audio"; readonly url: string }
  | { readonly kind: "pdf-image"; readonly url: string }
  | { readonly kind: "pdf-embed"; readonly url: string }
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "unavailable" };

export function VaultPreviewDialog({
  item,
  open,
  onOpenChange,
}: {
  item: VaultItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const [state, setState] = useState<PreviewState>({ kind: "loading" });
  const blobUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    for (const url of blobUrlsRef.current) URL.revokeObjectURL(url);
    blobUrlsRef.current = [];
    if (!open || !item) {
      setState({ kind: "loading" });
      return;
    }

    let active = true;
    setState({ kind: "loading" });
    const mimeType = resolveVaultMimeType(item);

    const trackUrl = (url: string) => {
      blobUrlsRef.current.push(url);
      return url;
    };

    void (async () => {
      if (usesVaultTextSnippetPreview(item)) {
        let text: string | null = null;
        if (item.hasText) text = await fetchVaultItemText(item.id);
        if (!text?.trim() && (isStructuredTextMime(mimeType) || isTextMime(mimeType))) {
          const file = await fetchVaultItemAsFile(item);
          if (file) text = await file.text();
        }
        if (!active) return;
        if (text?.trim()) {
          setState({ kind: "text", text: text.trim() });
          return;
        }
      }

      if (isImageMime(mimeType)) {
        let url: string | null = null;
        if (isHeicMime(mimeType)) {
          url = await convertHeicPreviewUrl(item);
        }
        if (!url) {
          url = await fetchVaultItemPreviewUrl(item);
        }
        if (!active) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (!url) {
          setState({ kind: "unavailable" });
          return;
        }
        setState({ kind: "image", url: trackUrl(url) });
        return;
      }

      if (isVideoMime(mimeType)) {
        const url = await fetchVaultItemPreviewUrl(item);
        if (!active) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (!url) {
          setState({ kind: "unavailable" });
          return;
        }
        setState({ kind: "video", url: trackUrl(url) });
        return;
      }

      if (isAudioMime(mimeType)) {
        const url = await fetchVaultItemPreviewUrl(item);
        if (!active) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (!url) {
          setState({ kind: "unavailable" });
          return;
        }
        setState({ kind: "audio", url: trackUrl(url) });
        return;
      }

      if (isPdfMime(mimeType)) {
        const thumb = await renderVaultPdfThumbnail(item);
        if (!active) return;
        if (thumb) {
          setState({ kind: "pdf-image", url: thumb });
          return;
        }

        let text: string | null = null;
        if (item.hasText) text = await fetchVaultItemText(item.id);
        if (!text?.trim()) text = await extractPdfTextSnippet(item);
        if (!active) return;
        if (text?.trim()) {
          setState({ kind: "text", text: text.trim() });
          return;
        }

        const embedUrl = await fetchVaultPdfEmbedUrl(item);
        if (!active) {
          if (embedUrl) URL.revokeObjectURL(embedUrl);
          return;
        }
        if (embedUrl) {
          setState({ kind: "pdf-embed", url: trackUrl(embedUrl) });
          return;
        }
        setState({ kind: "unavailable" });
        return;
      }

      if (isSpreadsheetFile(item)) {
        let text: string | null = null;
        if (item.hasText) text = await fetchVaultItemText(item.id);
        if (!text?.trim()) text = await fetchVaultSpreadsheetSnippet(item);
        if (!active) return;
        if (text?.trim()) {
          setState({ kind: "text", text: text.trim() });
          return;
        }
        setState({ kind: "unavailable" });
        return;
      }

      if (isOfficeFile(item)) {
        let text: string | null = null;
        if (item.hasText) text = await fetchVaultItemText(item.id);
        if (!text?.trim()) {
          const office = await fetchVaultOfficeSnippet(item);
          if (!active) return;
          if (office?.kind === "image") {
            setState({ kind: "image", url: trackUrl(office.url) });
            return;
          }
          text = office?.kind === "text" ? office.text : null;
        }
        if (!active) return;
        if (text?.trim()) {
          setState({ kind: "text", text: text.trim() });
          return;
        }
        setState({ kind: "unavailable" });
        return;
      }

      if (isArchiveFile(item)) {
        const text = await fetchVaultTextSnippet(item);
        if (!active) return;
        if (text?.trim()) {
          setState({ kind: "text", text: text.trim() });
          return;
        }
        setState({ kind: "unavailable" });
        return;
      }

      const url = await fetchVaultItemPreviewUrl(item);
      if (!active) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      if (url) {
        setState({ kind: "image", url: trackUrl(url) });
        return;
      }
      setState({ kind: "unavailable" });
    })();

    return () => {
      active = false;
      for (const url of blobUrlsRef.current) URL.revokeObjectURL(url);
      blobUrlsRef.current = [];
    };
  }, [open, item?.id]);

  const title = item?.name ?? "";
  const mimeLabel = item ? typeLabel(resolveVaultMimeType(item)) : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        showCloseButton={false}
        className="flex max-h-[min(90dvh,52rem)] w-full max-w-4xl flex-col overflow-hidden"
      >
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4">
          <DialogTitle className="truncate">{title}</DialogTitle>
          <DialogDescription>{mimeLabel}</DialogDescription>
        </DialogHeader>
        <DialogPanel className="min-h-0 flex-1 p-0">
          <div className="flex min-h-[min(60dvh,36rem)] items-center justify-center bg-muted/20 p-4">
            {state.kind === "loading" ? (
              <Spinner className="size-8 text-muted-foreground" />
            ) : state.kind === "image" || state.kind === "pdf-image" ? (
              <img
                src={state.url}
                alt=""
                className="max-h-[min(60dvh,36rem)] max-w-full object-contain"
              />
            ) : state.kind === "video" ? (
              <video
                src={state.url}
                controls
                // A blob/preview URL that 404s, decodes to nothing, or was
                // revoked under the element leaves a dead player, and play() on
                // it rejects with an unhandled NotSupportedError (Sentry
                // JAVASCRIPT-4). The element's error event is the handled path:
                // fall back to the download affordance.
                onError={() => setState({ kind: "unavailable" })}
                className="max-h-[min(60dvh,36rem)] max-w-full"
              />
            ) : state.kind === "audio" ? (
              <audio
                src={state.url}
                controls
                onError={() => setState({ kind: "unavailable" })}
                className="w-full max-w-lg"
              />
            ) : state.kind === "pdf-embed" ? (
              <embed
                src={state.url}
                type="application/pdf"
                className="h-[min(60dvh,36rem)] w-full min-h-[24rem] border-0"
              />
            ) : state.kind === "text" ? (
              <pre className="max-h-[min(60dvh,36rem)] w-full overflow-auto whitespace-pre-wrap break-words rounded-lg bg-background p-4 text-sm leading-6 text-foreground">
                {state.text}
              </pre>
            ) : (
              <div className="max-w-md space-y-3 text-center">
                <p className="text-sm text-muted-foreground">{t("vault.previewUnavailable")}</p>
                {item ? (
                  <Button type="button" size="sm" onClick={() => void downloadVaultItem(item)}>
                    {t("vault.download")}
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        </DialogPanel>
        <div className="flex shrink-0 justify-end gap-2 border-t border-border px-6 py-3">
          {item ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void downloadVaultItem(item)}
            >
              {t("vault.download")}
            </Button>
          ) : null}
          <DialogClose render={<Button type="button" size="sm" />}>{t("common.done")}</DialogClose>
        </div>
      </DialogPopup>
    </Dialog>
  );
}
